/**
 * Permission identifiers consumed by the backend authorisation gate and
 * the web-app hide-not-disable layer. Single source of truth so both
 * sides of the wire agree on every literal.
 *
 * Pattern: `{scope}:{module}:{entity}-{action}`
 *   - `scope`   — authorisation scope, derived directly from this segment
 *                 (ADR 0029): `workspace` (workspace-tier authority),
 *                 `organization` (organisation-tier authority), or
 *                 `backoffice` (admin / back-office RBAC namespace).
 *   - `module`  — bounded context (`identity`, `crm`, …).
 *   - `entity`  — aggregate / resource within the module.
 *   - `action`  — kebab-case verb (`create`, `update-address`, …).
 *
 * Prefer the CRUD verbs `create` / `read` / `update` / `delete` for the
 * `action`. A state transition or sub-action (mark-lost, archive, reopen,
 * advance-phase, …) is a `update` of the aggregate, not its own permission —
 * fold it into `{entity}-update` rather than minting a bespoke literal. Add a
 * custom action only with a *very* strong reason (e.g. it must be grantable
 * independently of general edit, like `organization:identity:api-key-revoke`). Keeping
 * the surface to CRUD avoids a permission explosion the role→permission config
 * would otherwise have to enumerate. (Reviewer-established convention, IMM-192.)
 *
 * A single sentinel sits alongside real permissions:
 *   - `'PUBLIC'` — no token required. Sign-in, callback, refresh, sign-out, health.
 *
 * Authentication-required-but-no-specific-permission used to live as an
 * `'AUTHENTICATED'` sentinel; it was removed once tier middleware
 * (`requireAdmin` / `requireWorkspaceMembership`) became the only gate
 * for non-`PUBLIC` routes. Routes that just need a logged-in caller
 * with no further permission name a thin permission like
 * `organization:identity:own-profile-read` or `backoffice:identity:own-profile-read`.
 *
 * Every route contract carries `permission: Permission`; the literal type
 * means a missing field is a compile error — the compile-time half of
 * default-deny.
 */
export type Permission =
  | 'PUBLIC'
  // backoffice permissions (admin / back-office RBAC namespace)
  | 'backoffice:identity:own-profile-read'
  | 'backoffice:identity:workspace-create'
  | 'backoffice:identity:workspace-read'
  | 'backoffice:identity:workspace-update'
  | 'backoffice:identity:organization-read'
  | 'backoffice:identity:organization-update'
  // Operator read access to the API-keys resource tree (IMM-414.2): the key
  // list/detail plus each key's request trail (list + per-request detail). A
  // back-office read literal like `organization-read`: default-denied via
  // `requireAdmin`, granted to the admin role. Gates all four routes, whose
  // request-detail returns lead PII in the stored bodies.
  | 'backoffice:identity:api-key-read'
  // Operator read access to the universal inbound-call log (IMM-457.2): the
  // global list of every `/v1` request across all workspaces plus a single
  // entry's detail with its stored bodies. DELIBERATELY distinct from
  // `api-key-read` — that grant exposes only partner API-key traffic, whereas
  // this store holds every member-session request body in the product, a wider
  // DSGVO surface. Reusing the key-read permission would silently widen what a
  // key-read grant sees. Default-denied via `requireAdmin`, granted to the admin
  // role.
  | 'backoffice:identity:api-call-read'
  // Operator capability to issue / revoke a workspace API key on a customer's
  // behalf from the admin app (IMM-435). Bespoke actions (not CRUD on a
  // back-office entity) like `member-impersonate`: each is a discrete,
  // separately-grantable power. Default-denied via `requireAdmin`, granted to
  // the admin role. `-create` gates `POST /v1/admin/api-keys` (+ the owner
  // resolve read); `-revoke` gates the admin revoke route (IMM-435.2);
  // `-update` gates the in-place edit of an issued key's granted permissions
  // and rate limit (IMM-493.1). `-update` is deliberately DISTINCT from
  // `-create`: every other admin PATCH carries its own `-update` literal, and
  // rewriting a live key partners already hold is a different power from
  // minting a fresh one. Nobody's access changes today — the V1 invariant
  // grants every active admin every `backoffice:*` permission.
  | 'backoffice:identity:api-key-create'
  | 'backoffice:identity:api-key-update'
  | 'backoffice:identity:api-key-revoke'
  // Operator capability to re-submit a rejected API-key request from its audit
  // trail (IMM-458). Separate from `api-key-read` because replaying WRITES —
  // it creates the lead the partner's original push failed to create — and
  // separate from `api-key-create`/`-revoke` because it is a discrete power
  // over customer data rather than over the key itself. Default-denied via
  // `requireAdmin`, granted to the admin role.
  | 'backoffice:identity:api-key-request-replay'
  // Operator capability to manage their OWN MCP backoffice credential —
  // the `adm_` bearer that lets an MCP client read the platform (IMM-522.1).
  // Three separate literals because minting and revoking are distinct powers
  // and the nav entry is gated on the read alone. Deliberately NOT reusing the
  // `api-key-*` grants: those govern customer-facing workspace keys bound to a
  // member, whereas these govern a cross-tenant, member-less operator
  // credential. Seeing and revoking OTHER operators' credentials is a further,
  // separate incident-response grant (IMM-522.4), not one of these.
  | 'backoffice:identity:mcp-access-read'
  | 'backoffice:identity:mcp-access-create'
  | 'backoffice:identity:mcp-access-revoke'
  // The ONE route an `adm_` MCP credential may call (IMM-522.2) — granted to
  // every credential automatically at mint, never chosen by an operator. Not a
  // `-read` because it gates the transport, not a resource; it is deliberately
  // NOT in the credential's grantable read catalogue.
  | 'backoffice:identity:mcp-invoke'
  // Incident response (IMM-522.4): see and revoke ANY operator's MCP
  // credential and flip the platform-wide surface switch. Distinct from the
  // self-service mcp-access grants, and never conferrable on a credential.
  | 'backoffice:identity:mcp-access-manage'
  // Operator containment for the CUSTOMER MCP surface (IMM-601.6) — the
  // platform-wide kill switch over the member-facing `/v1/mcp` endpoint.
  // Deliberately NOT the `mcp-access-*` grants above: those govern the
  // operator's OWN `adm_` backoffice credential, whereas these govern whether
  // customers' MCP-capable `immo_` keys are served at all. Split read/manage so
  // a disabled customer surface is diagnosable by every operator while only a
  // responder can flip it.
  | 'backoffice:identity:mcp-surface-read'
  | 'backoffice:identity:mcp-surface-manage'
  // The operator's page for the valuation agent's token (IMM-1402). Both in ADMIN_PERMISSIONS.
  | 'backoffice:identity:valuation-agent-token-read'
  | 'backoffice:identity:valuation-agent-token-manage'
  // The valuation agent's six use cases (IMM-1402). Held ONLY by a `vag_` token's grant — never by an
  // operator, never by any key. A custom action per literal, because each must be independently grantable.
  | 'backoffice:valuation:agent-catalog-read'
  | 'backoffice:valuation:agent-property-read'
  | 'backoffice:valuation:agent-request-receive'
  | 'backoffice:valuation:agent-result-deliver'
  | 'backoffice:valuation:agent-finding-write'
  | 'backoffice:valuation:agent-capture-photo-read'
  // Operator read over the OAuth grants customers' MCP clients hold
  // (IMM-625.4) — the admin-tier twin of the member's own connected-clients
  // list, and the read that makes an OAuth grant nameable in the reachability
  // diagnosis at all. Deliberately NOT `mcp-surface-read`: this is a read of
  // customer CREDENTIALS, not of the MCP surface's configuration, and it will
  // outlive the panel that needed it — so it is grantable separately, keeping
  // ADR 0029's `{scope}:{module}:{entity}-{action}` shape honest. Read-only by
  // design: revoking a grant stays the member's own screen.
  | 'backoffice:identity:connected-client-read'
  // Cross-tenant read over the platform audit trail, conferred on MCP
  // backoffice credentials for `audit_event_search` (IMM-522.2). Also gates the
  // operator's e-mail log (IMM-1076): the log is visible to whoever may read audit events.
  | 'backoffice:identity:audit-event-read'
  // Platform-history reads over the event outbox and the recorded access
  // decisions, conferred on MCP backoffice credentials (IMM-522.3). Scoped
  // `shared` because both stores are kernel-owned, not a module's.
  | 'backoffice:shared:event-delivery-read'
  | 'backoffice:shared:access-decision-read'
  // Cross-tenant lead read for the MCP wave-1 entity tools (IMM-522.5).
  | 'backoffice:lead:lead-read'
  // The authoritative permission-derivation explain tool (IMM-522.6).
  | 'backoffice:identity:permission-explain-read'
  | 'backoffice:identity:member-read'
  | 'backoffice:identity:member-create'
  | 'backoffice:identity:member-update'
  // Admin capability to start an impersonation session against a workspace
  // member (IMM-346). A bespoke action (not CRUD) like `api-key-revoke`: it
  // is a discrete, separately-grantable power, not a write to an entity.
  | 'backoffice:identity:member-impersonate'
  | 'backoffice:identity:role-read'
  | 'backoffice:admin:read'
  | 'backoffice:admin:create'
  | 'backoffice:admin:deactivate'
  // An operator's own table preferences on admin.immoos.ai (IMM-1074.1). Self-scoped:
  // the routes resolve the operator from the session, so the grant reaches nobody else's.
  | 'backoffice:admin:preferences-read'
  | 'backoffice:admin:preferences-update'
  // Registering the operator's own iPhone for pushes (IMM-1411.3). Self-scoped like `preferences-*`:
  // the route resolves the operator from the session.
  | 'backoffice:admin:push-device-manage'
  // Seeing and starting LandIQ staging data runs (IMM-1132). One literal for both, so it is
  // not split into `-read` / `-start`; `-manage` because it covers a write. It reaches no
  // tenant data, which is why it is a `PLATFORM_ACTION_PERMISSIONS` member on the admin MCP.
  | 'backoffice:admin:landiq-data-run-manage'
  | 'backoffice:property:property-read'
  // `document-write` gates FILING a Dokument on an Objekt from the machine
  // surface (IMM-587.4). Deliberately separate from `property-read`: a read
  // literal authorising a write is a lie the next reader trips over, and it
  // would make a read-only operator reach over Objekte impossible to issue.
  | 'backoffice:property:document-write'
  // Correcting, editing, adding and deleting Objekt-memory entries on the admin MCP (IMM-1157).
  // A write to tenant data; on a backoffice credential it is opt-in at mint
  // (`MCP_ACCESS_OPTIONAL_GRANTS`) and a `TENANT_DATA_WRITE_PERMISSIONS` member.
  | 'backoffice:property:memory-manage'
  // Operator download of any property document from the admin MCP (IMM-1156.3). Its own literal rather
  // than `property-read`, so a credential can read Objekte without being handed every customer file.
  | 'backoffice:property:document-read'
  // Operator rename / recategorise of any property document from the admin MCP (IMM-1156.3). Opt-in at mint
  // (`MCP_ACCESS_OPTIONAL_GRANTS`) and a `TENANT_DATA_WRITE_PERMISSIONS` member.
  | 'backoffice:property:document-update'
  // Operator access to soft-deleted deals (IMM-851.1). `-read` gates the cross-tenant
  // deleted list, `-restore` the restore. Today every active admin holds every
  // `backoffice:*` literal, so the split documents intent rather than enforcing it.
  | 'backoffice:deal:deal-read'
  | 'backoffice:deal:deal-restore'
  // The operator's Standort record move (IMM-1104). `-read` lists a Standort's deals,
  // Objekte, Kontakte and Leads; `-move` previews and performs the move. Deal-module
  // literals because the deal module orchestrates the move across the four kinds.
  | 'backoffice:deal:organization-record-read'
  | 'backoffice:deal:organization-record-move'
  // Operator label read over Interessenten (IMM-1120, Refined Decision 1): gates an Interessent's label, the Datenraum card (`prospect_data_room`, IMM-1176.2) and the prospect_detail read (IMM-1172) on the admin MCP.
  | 'backoffice:deal:prospect-read'
  // Operator read over soft-deleted companies (IMM-1096.4).
  | 'backoffice:relationship:company-read'
  // Operator label read over Kontakte (IMM-1120, Refined Decision 1): gates a Kontakt's label on the admin MCP; no tool reads a Kontakt with it.
  | 'backoffice:relationship:contact-read'
  // Operator access to the cross-tenant valuation queue (IMM-407.4). Back-office
  // reads/actions gated by `requireAdmin`, granted to the admin role via
  // `ADMIN_PERMISSIONS`. `-read` gates the queue list + the request detail;
  // `-take-on` gates claiming a REQUESTED request (REQUESTED → IN_PROGRESS). A
  // bespoke `-take-on` action (not CRUD) like `member-impersonate`: it is a
  // discrete, separately-grantable power, not a write to an entity.
  | 'backoffice:valuation:valuation-request-read'
  | 'backoffice:valuation:valuation-request-take-on'
  // `-complete` gates PRODUCING the valuation (IN_PROGRESS → AWAITING_RELEASE):
  // recording the result and the report pair and submitting them for approval
  // (IMM-407.5, reshaped by IMM-475.2 — the literal is unrenamed because it still
  // gates exactly "may produce a valuation"). A discrete, separately-grantable
  // power like `-take-on`, not a CRUD write.
  | 'backoffice:valuation:valuation-request-complete'
  // `-release` gates the operator side of the approval gate (IMM-475.2, AC 10):
  // releasing an AWAITING_RELEASE valuation — which is what delivers it to the
  // Objekt and notifies the agent — returning it for rework, and adjusting its held
  // figures. Deliberately separate from `-complete`: producing a valuation and
  // approving one are different powers, and an operator may hold either alone.
  | 'backoffice:valuation:valuation-request-release'
  // `-create` gates RAISING a request on the machine surface (IMM-587.2): create
  // the draft on a Deal, fill it, submit, withdraw, delete, and attach its
  // Unterlagen. None of the four literals above covers it — they all describe
  // acting on a request somebody else raised — so a consumer that only produces
  // valuations is never given this one.
  | 'backoffice:valuation:valuation-request-create'
  // Operator management of product-managed „Skills" in the admin app (IMM-406):
  // the document-analysis prompt surface here, and IMM-436's „Agent-Skills"
  // family later. Permissions gate by audience *tier*, not by skill family:
  // `product-skill:*` covers product-managed skills product staff may tune,
  // while `system-skill:*` (reserved, not yet minted) fences off system-level
  // skills like the chat core. `-read` gates the Skills list + a skill's detail;
  // `-manage` gates the edit / activate / deactivate writes (IMM-406.3). Read is
  // split from manage so a read-only operator can see the surface without edit
  // rights. Default-denied via `requireAdmin`, granted to the admin role.
  | 'backoffice:product-skill:read'
  | 'backoffice:product-skill:manage'
  // Fleet machine registry (IMM-910.1, ADR 0047): the shared enrolment key
  // authenticates a join request and authorises nothing — approval is a human
  // act. `-read` gates the machine list + detail; `-command` gates approve /
  // reject / revoke. Approval sits under `-command`, not a bespoke third
  // literal: approving a machine and commanding one are the same trust
  // decision (ADR 0047).
  | 'backoffice:fleet:read'
  | 'backoffice:fleet:command'
  // The operator's WhatsApp lists (IMM-928.4): a workspace's failed dispatches
  // and the platform-wide unmatched inbound. One literal for both reads — the
  // unmatched list spans tenants, so it is never a member-tier permission.
  | 'backoffice:communications:whatsapp-dispatch-read'
  // workspace-scoped permissions (workspace-tier authority)
  | 'workspace:identity:organization-create'
  // Manual assign/remove of a Standort's Einzugsgebiet PLZs (IMM-267). Workspace-
  // scoped so `ALL_WORKSPACE_PERMISSIONS` grants it to owner/admin only — a
  // Standortleitung (org role) is refused even on their own Standort (the
  // allocation is a workspace-level decision). Reads reuse organization-read.
  | 'workspace:identity:organization-coverage-write'
  // Read a workspace's whole Einzugsgebiet — every Standort with its assigned
  // PLZs — via the authenticated coverage API (IMM-415). Workspace-scoped (the
  // read is workspace-wide on a `/workspaces/:workspaceId/…` path with no target
  // org, so the api-key gate uses the key's permissions as-is rather than
  // intersecting org roles) and auto-granted to owner/admin via
  // `ALL_WORKSPACE_PERMISSIONS`. The grant that lets an `immo_` key call the
  // coverage endpoint; added to the customer key-issuance allow-list in IMM-415.2.
  //
  // Both literals say `coverage`: the permission a customer grants an `immo_`
  // key is listed next to the endpoint it unlocks
  // (`GET /v1/workspaces/:workspaceId/coverage`), so the two have to agree.
  // Renamed from the earlier spelling of the concept (see identity migration
  // 0028 for the verbatim old literals); issued keys and custom roles carrying
  // the old strings were rewritten by that migration. Do NOT rename these
  // again — they are stored verbatim in `api_keys.granted_permissions`, so a
  // second rewrite would break every live key.
  | 'workspace:identity:organization-coverage-read'
  | 'workspace:identity:workspace-role-assign'
  // GET /workspaces/:wid/organizations — full org tree (roles + descendants). For
  // admin/owner surfaces; checked by the navbar Standorte link visibility.
  | 'workspace:identity:organizations-read'
  // Exchange a handoff token for the identity it names (IMM-751.2.2). Workspace-
  // tier because the credential IS a workspace key and the capability is not
  // Standort-shaped — the route carries no organisation, and the token itself
  // carries whichever Standort it recorded at mint time. Grantable, unlike its
  // `organization:identity:app-handoff-create` counterpart: this half is called
  // by the receiving application with its own key, never by a member session.
  | 'workspace:identity:app-handoff-exchange'
  // Self-service member permissions — held implicitly by any workspace member (no org role
  // required). Workspace-scoped because the routes they gate (/me/profile, /me/onboarding,
  // org list/get) are workspace-addressed and need no per-org assignment.
  | 'workspace:identity:organization-read'
  // GET /workspaces/:wid/me/organizations — flat list of the calling member's own
  // affiliated orgs. Implicit for all members; used by the navbar org-switch.
  | 'workspace:identity:my-organizations-read'
  | 'workspace:identity:profile-read'
  | 'workspace:identity:profile-update'
  // GET/PUT /workspaces/:wid/me/preferences/:key — the calling member's own UI
  // preferences (per-view filter state, etc). Implicit for every member: the KV
  // store is private and self-scoped, so it needs no org role.
  | 'workspace:identity:preferences-read'
  | 'workspace:identity:preferences-update'
  | 'workspace:hlre:area-read'
  | 'workspace:hlre:partner-lead-read'
  | 'workspace:hlre:partner-profile-read'
  | 'workspace:identity:member-read'
  | 'workspace:identity:member-create'
  | 'workspace:identity:member-update'
  | 'workspace:identity:member-profile-read'
  | 'workspace:identity:member-profile-update'
  | 'workspace:identity:invitation-read'
  | 'workspace:identity:invitation-resend'
  | 'workspace:identity:invitation-revoke'
  | 'workspace:identity:company-read'
  | 'workspace:identity:company-update'
  | 'workspace:identity:branding-read'
  | 'workspace:identity:branding-update'
  | 'workspace:identity:document-template-read'
  | 'workspace:identity:document-template-create'
  | 'workspace:identity:document-template-update'
  | 'workspace:identity:document-template-delete'
  | 'workspace:identity:email-template-read'
  | 'workspace:identity:email-template-create'
  | 'workspace:identity:email-template-update'
  | 'workspace:identity:email-template-delete'
  | 'workspace:identity:api-key-read'
  | 'workspace:identity:api-key-create'
  | 'workspace:identity:api-key-update'
  | 'workspace:identity:api-key-revoke'
  // Connect an MCP client through OAuth (consent/decline), list the member's own
  // connected clients, and revoke one. Member-implicit — every member of every
  // role holds them — because a connected client acts as the person who approved
  // it and nobody else: the grant is capped to that member's own live
  // permissions at consent and on every call. Split from `api-key-*`, which
  // stays Owner/Admin territory, so connecting Claude does not require the
  // authority to mint workspace keys. Session-only: never grantable to a key.
  | 'workspace:identity:connected-client-create'
  | 'workspace:identity:connected-client-read'
  | 'workspace:identity:connected-client-revoke'
  // The ONE route an MCP-capable `immo_` workspace key may call (IMM-601.1) —
  // the workspace-tier twin of `backoffice:identity:mcp-invoke`. Injected by
  // `CryptoApiKeyVerifier` into an MCP-capable key's effective permissions at
  // verify time, never stored in `granted_permissions`, never offerable in the
  // issuance form (absent from `API_KEY_GRANTABLE_PERMISSIONS`), and held by NO
  // role — it is excluded from the derived workspace-role catalogue and from
  // `ADMIN_API_KEY_GRANTABLE_PERMISSIONS`, so the only path to it is an
  // MCP-capable key. That is what lets the MCP endpoint (IMM-601.2) refuse
  // every other credential uniformly.
  | 'workspace:identity:mcp-invoke'
  // GET /workspaces/:wid/<entity> — the visible-Standort list endpoints (IMM-20.4).
  // Workspace-scoped because the routes are workspace-addressed and scope records to
  // the caller's visible org set (affiliated orgs + descendants, plus assigned-to-self)
  // rather than a single per-org assignment. Implicit for every member (see
  // MEMBER_IMPLICIT_PERMISSIONS).
  | 'workspace:relationship:contact-read'
  // GET /workspaces/:wid/companies — the visible-Standort company list (IMM-4).
  // The CRM Company (relationship module), distinct from the organisation's own
  // legal entity under the `identity` namespace. Implicit for every member.
  | 'workspace:relationship:company-read'
  | 'workspace:property:property-read'
  // The workspace-tier twin of `organization:property:document-read`, for
  // workspace-addressed surfaces that read documents across the caller's visible
  // Standorte — today the ⌘K palette's Dokumente source (IMM-547). Documents are
  // the only palette entity with NO workspace-scoped list route (unlike
  // properties/contacts/deals/leads), so before IMM-547 that source had only the
  // org literal to gate on and could never pass on a workspace-scoped route: the
  // resolver's `resolveWorkspace()` branch cannot produce an `organization:*`
  // literal, so the group was absent for every member. Keep the two in step —
  // the pairing is asserted in `property-document-search-source.test.ts`.
  | 'workspace:property:document-read'
  // The workspace's ImmoScout24 connection (IMM-1057.1). `read` is implicit for
  // every member (the state is shown read-only); `manage` is owner/admin only
  // and never API-key grantable — an operator or key must not bind a
  // customer's portal account.
  | 'workspace:property:portal-connection-read'
  | 'workspace:property:portal-connection-manage'
  | 'workspace:deal:deal-read'
  | 'workspace:deal:prospect-read'
  // IMM-1431.1 — reading the workspace's Käufer-Maklervertrag Vorlagen. Implicit for every member; never API-key grantable (internal routes; agents read through `document_template_*`).
  | 'workspace:deal:buyer-contract-template-read'
  // IMM-1431.2 — adapting the standard into the workspace's own Käufer Vorlage. Owner / Admin only; internal, never API-key grantable.
  | 'workspace:deal:buyer-contract-template-create'
  // IMM-1431.2 — saving a new version of the own Käufer Vorlage. Owner / Admin only; internal, never API-key grantable.
  | 'workspace:deal:buyer-contract-template-update'
  // IMM-1431.5 — deleting the own Käufer Vorlage. Owner / Admin only; internal, never API-key grantable.
  | 'workspace:deal:buyer-contract-template-delete'
  | 'workspace:lead:lead-read'
  // POST /workspaces/:wid/<entity>/:id/standort — cross-Standort reassignment
  // (IMM-20.4 AC22). Workspace-scoped and implicit for every member like the
  // read literals above; the real authority is the command handler's authz rule
  // (Owner/Workspace-Admin OR the record's creator OR both source+target orgs in
  // the caller's visible set), not this gate.
  | 'workspace:relationship:contact-reassign'
  | 'workspace:property:property-reassign'
  | 'workspace:deal:deal-reassign'
  | 'workspace:lead:lead-reassign'
  // assistant (IMM-307.1). Workspace-scoped and implicit for every member: a
  // conversation is member-private, spanning the member's whole org visibility
  // rather than one Standort, so it is workspace-addressed and owner-scoped in
  // the handler. `-use` gates create/post, `-read` gates list/get.
  | 'workspace:assistant:conversation-use'
  | 'workspace:assistant:conversation-read'
  // The `record_capability_gap` tool (IMM-349.1). It has no HTTP route — it is
  // reachable only as an assistant tool — but it became a REAL literal in
  // IMM-436.4 because a tool's `requiredPermission` is now a live gate: a skill
  // run dispatches only tools its frozen snapshot holds, so a fictional string
  // would make the tool permanently undispatchable. Implicit, like its
  // `conversation-*` neighbours, so the assistant records the gap for every
  // member rather than only for owners and admins.
  | 'workspace:assistant:capability-gap-record'
  // Agent-skill instances (IMM-436.2): configure, grant and revoke a skill's
  // standing permission to act as the granting member. DELIBERATELY NOT in
  // `MEMBER_IMPLICIT_PERMISSIONS`, unlike its `conversation-*` neighbours — so
  // only `workspace-owner` / `workspace-admin`, who hold every WORKSPACE
  // literal, can hold a skill instance in this release. Since the grant is
  // personal (only the granting member may confirm their own), that also means
  // an ordinary agent cannot be granted one. See IMM-436.2's plan.
  | 'workspace:assistant:skill-manage'
  // Reading a skill instance's run history and continuity state (IMM-436.3).
  // Split from `-manage` so a reviewer can inspect what a skill did without
  // the ability to trigger it. Non-implicit for the same reason `-manage` is.
  | 'workspace:assistant:skill-read'
  // GET /workspaces/:wid/search — the command palette (IMM-448). Implicit for
  // every member and DELIBERATELY inert: it never denies anyone, it exists only
  // because a contract leaf must name a permission. What a member actually sees
  // is decided per entity, inside each search source, against that entity's own
  // read literal — so this gate cannot drift away from "searchable iff
  // openable" the way a real `search:global:read` capability could.
  | 'workspace:search:search-read'
  // organisation-scoped permissions (granular role-based assignment lands with IMM-22)
  | 'organization:identity:own-profile-read'
  | 'organization:identity:organization-read'
  | 'organization:identity:organization-update'
  | 'organization:identity:member-read'
  // The members of ONE Standort, addressed by that Standort (IMM-751.3).
  //
  // Spelled `location-`, not `standort-`, which is what the slice pinned: a
  // permission literal is a code identifier AND published copy — it is rendered
  // verbatim in the API reference — and both rules forbid the German word there.
  // The published-copy guard in `api-docs/src/openapi.test.ts` is what caught
  // it. „Standort" survives where it belongs, in the i18n label.
  //
  // A NEW
  // literal rather than a widening of `member-read` above, which parent
  // Resolved Decision 15 chose: `member-read` also unhides the workspace Members
  // page and the Standort „Mitglieder" tab, so widening it would hand every
  // Agent, Assistant and Viewer in every workspace a second employee-PII surface
  // as a side effect of an integration ticket. Held by every org role (the three
  // presets carry it explicitly; organisation-owner via the derived
  // `ALL_ORG_PERMISSIONS`), and grantable to a key.
  | 'organization:identity:location-member-read'
  | 'organization:identity:member-create'
  | 'organization:identity:member-update'
  | 'organization:identity:member-role-assign'
  | 'organization:identity:profile-read'
  | 'organization:identity:profile-update'
  | 'organization:identity:member-profile-read'
  | 'organization:identity:member-profile-update'
  | 'organization:identity:company-read'
  | 'organization:identity:company-update'
  // Start a handoff into an approved linked application (IMM-751.2.1). Org-tier
  // because the audience is the Standortleitung — an ORGANISATION role — plus the
  // workspace owner/admin, and no workspace-tier literal can name that set.
  // Organisation-owner holds it through the derived `ALL_ORG_PERMISSIONS`, the
  // workspace owner/admin through the resolver's `grantsGlobalAccess`;
  // deliberately absent from the agent / assistant / viewer presets, since a
  // crossing is an ordering action and not everyone may order.
  //
  // Deliberately NOT in `API_KEY_GRANTABLE_PERMISSIONS`: minting needs a signed-in
  // member session, so a key holding it could mint a crossing for nobody.
  | 'organization:identity:app-handoff-create'
  // No org-scoped branding permission: visual identity (Branding & CI) is a
  // workspace-level record only — see `workspace:identity:branding-{read,update}`.
  // There is no per-organisation visual identity, so no org-tier grant exists.
  | 'organization:identity:api-key-read'
  | 'organization:identity:api-key-create'
  | 'organization:identity:api-key-update'
  | 'organization:identity:api-key-revoke'
  | 'organization:media:file-write'
  | 'organization:media:file-read'
  | 'organization:media:file-delete'
  | 'organization:property:property-create'
  | 'organization:property:property-read'
  | 'organization:property:property-update'
  | 'organization:property:portal-listing-publish'
  | 'organization:property:portal-listing-withdraw'
  // Write an Objekt to ImmoScout24 from the browser extension and record what it reports (IMM-1381, ADR 0064).
  // Its own literal so an extension key reaches neither the web exposé nor the OAuth transmit routes.
  | 'organization:property:portal-listing-browser-publish'
  | 'organization:property:property-reassign'
  | 'organization:property:property-delete'
  | 'organization:property:property-contact-write'
  | 'organization:property:document-read'
  | 'organization:property:document-write'
  // Raise a Dokumentenanforderung — request missing documents from the Objekt's
  // Eigentümer (IMM-383.2). Non-Viewer: held by agent / Owner / Admin, absent
  // from viewer. Viewing the Unterlagen-Check panel needs only `document-read`.
  | 'organization:property:document-request'
  // Manage ANY property document, not just one's own (IMM-218.3). The uploader
  // may always rename / re-categorise / delete their own document via
  // `document-write`; this literal extends that to every document on any Objekt
  // in the workspace. Held only by Owner / Admin — auto-granted to the
  // organisation-owner via `ALL_ORG_PERMISSIONS` and to workspace-admin/owner via
  // the resolver's `grantsGlobalAccess`; deliberately absent from agent /
  // assistant / viewer. Grantable to a key since IMM-1156.1 — effective only
  // where the bound member holds it at the Standort the call names.
  | 'organization:property:document-manage-any'
  // Property media — read (view the gallery) / write (upload) — IMM-219.1.
  | 'organization:property:media-read'
  | 'organization:property:media-write'
  // Manage / delete ANY property media, not just one's own (IMM-219.3). The
  // uploader may always delete their own media via `media-write`; this literal
  // extends that to every media item on any Objekt in the workspace. Held only
  // by Owner / Admin — auto-granted to the organisation-owner via
  // `ALL_ORG_PERMISSIONS`; deliberately absent from agent / assistant / viewer.
  | 'organization:property:media-manage-any'
  | 'organization:deal:deal-create'
  | 'organization:deal:deal-read'
  | 'organization:deal:deal-phase-advance'
  | 'organization:deal:deal-phase-move-back'
  | 'organization:deal:deal-update'
  | 'organization:deal:prospect-create'
  | 'organization:deal:prospect-phase-advance'
  | 'organization:deal:prospect-phase-move-back'
  | 'organization:deal:prospect-update'
  | 'organization:deal:deal-delete'
  // Replace a deal's responsible-member set (IMM-402). Org-scoped, mirroring
  // `organization:lead:lead-assign`: Owner/Workspace-Admin hold it via
  // `grantsGlobalAccess` → `ALL_ORG_PERMISSIONS`, a Standortleitung via the
  // organisation-owner role on their Standort; absent from agent/assistant/viewer.
  | 'organization:deal:deal-assign'
  // Read-only oversight of the Standort's whole pipeline — the Deal-Überblick
  // (IMM-430.7). It WIDENS scope rather than gating a door: without it the
  // overview still serves, narrowed to deals the caller is responsible for, so
  // the endpoint stays behind plain `deal-read`. Deliberately not `deal-assign`,
  // which is the power to reassign, not the right to look.
  | 'organization:deal:deal-oversight'
  // Delete ANY member's generated document on a Deal, not just one's own
  // (IMM-268.4). The creator may always delete their own; this literal extends
  // that to every document. Owner / Admin only — via `ALL_ORG_PERMISSIONS` and
  // `grantsGlobalAccess`; absent from agent / assistant / viewer. Not in
  // `API_KEY_GRANTABLE_PERMISSIONS`, because deletion is never a tool.
  | 'organization:deal:document-manage-any'
  // Bewertung / Anfrage (IMM-407). Org-scoped, mirroring the deal read/create
  // split: `-read` gates the deal's Bewertung tab + its read endpoint (held
  // broadly like `deal-read`); `-create` gates raising a „Bewertung anfragen"
  // Anfrage (held by the roles that hold `deal-create`). The `-create` endpoint
  // lands in IMM-407.2.1; the literal is defined here so the tab's action can
  // gate on it from this slice.
  | 'organization:valuation:valuation-request-read'
  | 'organization:valuation:valuation-request-create'
  // `-release` gates the in-tenant side of the approval gate (IMM-475.2, AC 9):
  // releasing a valuation that waits for Freigabe, returning it for rework, and
  // adjusting its held figures. Held by the Standortleitung (organisation-owner)
  // and, through `grantsGlobalAccess`, by the workspace admin/owner — deliberately
  // absent from the agent / assistant / viewer presets, since the whole point of
  // the gate is that the requesting agent is not the one who approves the number.
  | 'organization:valuation:valuation-request-release'
  | 'organization:activity:activity-read'
  | 'organization:activity:activity-update'
  // Write a plannable activity to the shared store (IMM-430.1 / ADR 0035).
  // Deliberately subject-agnostic: a caller who may write activities may write
  // them for any subject they can already reach, and reachability is
  // `organizationId` scoping (a 404), not a second capability check. Held by the
  // roles that hold `organization:relationship:contact-update` today, since a
  // deal note currently rides that literal — so this mints no new authority.
  | 'organization:activity:activity-write'
  // Read-only oversight of the Standort's whole activity feed (IMM-776.1). It
  // WIDENS scope rather than gating a door — the route stays behind plain
  // `activity-read`, and without this literal the caller sees only their own
  // work. Mirrors `organization:deal:deal-oversight`, but kept as its own
  // literal rather than reused: this feed spans Leads, Deals, Kontakte, Objekte
  // and Bewertungsanfragen, so naming a *deal* capability as the gate for all of
  // them would be wrong the first time a tenant composes a custom role wanting a
  // Standortleitung who oversees deals but not everyone's notes.
  | 'organization:activity:activity-oversight'
  // Removes another member's synced e-mail activity from a timeline (IMM-866.8).
  // Owner-only: held through ALL_ORG_PERMISSIONS and `grantsGlobalAccess`, absent
  // from the agent, assistant and viewer presets and from the customer grantable
  // list. Half of the author-or-Owner rule; the Postfach owner needs only
  // `activity-update`.
  | 'organization:activity:activity-delete'
  | 'organization:relationship:contact-create'
  | 'organization:relationship:contact-read'
  | 'organization:relationship:contact-update'
  | 'organization:relationship:contact-delete'
  | 'organization:relationship:contact-note-update'
  | 'organization:relationship:contact-note-delete'
  | 'organization:relationship:contact-communication-update'
  | 'organization:relationship:contact-communication-delete'
  // CRM Company (IMM-4), org-addressed writes. Distinct from the organisation's
  // own legal entity under the `identity` namespace. Archive / delete literals
  // land with the lifecycle slice (IMM-4.2).
  | 'organization:relationship:company-create'
  | 'organization:relationship:company-read'
  | 'organization:relationship:company-update'
  // Company lifecycle (IMM-4.2). `archive` is broad (agent+); `unarchive` and
  // `delete` (soft-delete) are Owner/Admin-only — present only in
  // ALL_ORG_PERMISSIONS, absent from the agent/assistant/viewer presets.
  | 'organization:relationship:company-archive'
  | 'organization:relationship:company-unarchive'
  | 'organization:relationship:company-delete'
  // Manage the contact roster of a company (IMM-4.3): link / unlink / edit-role /
  // set-primary. One write literal (mirrors organization:property:property-contact-write).
  | 'organization:relationship:company-contact-write'
  | 'organization:lead:lead-create'
  | 'organization:lead:lead-read'
  | 'organization:lead:lead-update'
  | 'organization:lead:lead-assign'
  | 'organization:lead:lead-partner-read'
  | 'organization:lead:lead-partner-manage'
  // Send email from the member's OWN connected mailbox (IMM-233.2 / ADR 0018). A
  // WRITE: sending is the two-way half of the mailbox surface, so it must separate
  // the roles that may act (agent / assistant / owner-admin) from a read-only
  // Viewer. Org-scoped — not workspace-scoped — because that is the only tier whose
  // machinery grants it to agent/assistant while withholding it from viewer: a
  // workspace literal would reach owner/admin only, and making it member-implicit
  // (like `mailbox-thread-read`) would hand it to viewer too. Owner/Admin hold it
  // via `grantsGlobalAccess` → `ALL_ORG_PERMISSIONS`. A member always sends from
  // their own mailbox (no send-on-behalf-of, IMM-233 Resolved Decision §7), so
  // there is no foreign-mailbox axis — this literal is the whole send gate. The
  // send route is workspace-addressed (email is workspace-scoped, not org-scoped);
  // the resolver's primary-org fallback resolves the caller's role for the gate.
  | 'organization:communications:mailbox-send'
  // Create an appointment in the member's own connected calendar (IMM-234.4).
  // Org tier for the same reason as `mailbox-send`: the only tier that grants
  // agent/assistant while withholding it from a read-only Viewer. Deliberately not
  // `workspace:communications:calendar-write`, which is Owner/Admin-only.
  | 'organization:communications:appointment-write'
  // Member mailbox connect/disconnect (IMM-86.3 / ADR 0018). Workspace-scoped,
  // so auto-granted to Owner/Admin — and MEMBER-IMPLICIT since IMM-852, because
  // the grant is the member's own: gating it on workspace authority left an agent
  // unable to connect any mailbox at all. Being member-implicit, they are
  // unioned into EVERY member's principal unconditionally: two discrete literals
  // (two routes, attach and revoke), but neither can be withheld from a member by
  // any role, custom or otherwise. Withholding a mailbox capability from a role is
  // possible only on the org tier — see `mailbox-send` above, which is org-scoped
  // for exactly that reason.
  | 'workspace:communications:mailbox-connect'
  | 'workspace:communications:mailbox-disconnect'
  // Read another member's synced mailbox threads (IMM-233.1.1 / ADR 0018). A
  // member always reads their OWN mailbox with no permission; this workspace-
  // scoped capability gates reading a colleague's threads, so it is auto-granted
  // to Owner/Admin via `ALL_WORKSPACE_PERMISSIONS`. Enforced by 233.1.2's routes.
  | 'workspace:communications:mailbox-read'
  // List a workspace's company mailboxes and read their mail (IMM-264). Workspace-
  // tier: the Owner/Admin role is the gate, via `ALL_WORKSPACE_PERMISSIONS`. Not
  // member-implicit and not in the customer `API_KEY_GRANTABLE_PERMISSIONS`;
  // offerable on operator-issued workspace keys like `mailbox-read`, usable only
  // through an owner/admin-bound key.
  | 'workspace:communications:company-mailbox-read'
  // Connect, reconnect and disconnect a company mailbox (IMM-264). Same tier and
  // holders as `company-mailbox-read`, kept separate so reading can be granted
  // without lifecycle control.
  | 'workspace:communications:company-mailbox-manage'
  // Base gate for the thread-read API (IMM-233.1.2). Member-implicit — EVERY
  // workspace member holds it — so the thread / body / mark-read / attachment
  // routes are membership-gated without blocking a member from reading their OWN
  // mailbox. The own-vs-foreign decision is enforced in-handler: own → allowed;
  // foreign → additionally requires `mailbox-read` (above). It carries no
  // authority of its own beyond "this caller is a workspace member".
  | 'workspace:communications:mailbox-thread-read'
  // Open the workspace event stream (IMM-516.2 / ADR 0036). Member-implicit for
  // the same reason as `mailbox-thread-read`: every member may hold a connection,
  // and `attachVisibleOrganizations` — resolved once at connect — decides which
  // Standorte that connection may hear about, exactly as it scopes the
  // workspace-addressed list reads. The literal carries no authority of its own
  // beyond "this caller is a workspace member".
  | 'workspace:communications:event-stream-read'
  // Read the member's OWN calendar (IMM-234.2). Member-implicit: the routes are
  // scoped by (workspace, member), so the literal carries no authority over anyone
  // else — a plain workspace literal would reach Owner/Admin only (IMM-852).
  | 'workspace:communications:calendar-read'
  // Create, change and cancel appointments (first read by IMM-234.2 as the
  // Owner/Admin half of `canEdit`). Deliberately NOT member-implicit; the write
  // routes of IMM-234.4 must revisit the tier before gating on it.
  | 'workspace:communications:calendar-write'
  // Partner-onboarding participant (IMM-255.3). Held by no member/role: the sole
  // authorizer is the `requireOnboardingParticipant` middleware, which grants
  // access when the authenticated user's e-mail matches the path onboarding's
  // location manager. A matching, owned onboarding IS the authorization — there
  // is no member-permission check behind this literal.
  | 'organization:identity:partner-onboard';

/**
 * Every real `Permission` literal (the `PUBLIC` sentinel excluded). Runtime
 * companion to the `Permission` union for callers that must enumerate the full
 * set and filter by `permissionScope()` (e.g. the workspace-role catalogue).
 * Kept exhaustive by the `satisfies` check below.
 */
export const PERMISSIONS = [
  'backoffice:identity:own-profile-read',
  'backoffice:identity:workspace-create',
  'backoffice:identity:workspace-read',
  'backoffice:identity:workspace-update',
  'backoffice:identity:organization-read',
  'backoffice:identity:organization-update',
  'backoffice:identity:api-key-read',
  'backoffice:identity:api-call-read',
  'backoffice:identity:api-key-create',
  'backoffice:identity:api-key-update',
  'backoffice:identity:api-key-revoke',
  'backoffice:identity:api-key-request-replay',
  'backoffice:identity:mcp-access-read',
  'backoffice:identity:mcp-access-create',
  'backoffice:identity:mcp-access-revoke',
  'backoffice:identity:mcp-invoke',
  'backoffice:identity:mcp-access-manage',
  'backoffice:identity:mcp-surface-read',
  'backoffice:identity:mcp-surface-manage',
  'backoffice:identity:valuation-agent-token-read',
  'backoffice:identity:valuation-agent-token-manage',
  'backoffice:valuation:agent-catalog-read',
  'backoffice:valuation:agent-property-read',
  'backoffice:valuation:agent-request-receive',
  'backoffice:valuation:agent-result-deliver',
  'backoffice:valuation:agent-finding-write',
  'backoffice:valuation:agent-capture-photo-read',
  'backoffice:identity:connected-client-read',
  'backoffice:identity:audit-event-read',
  'backoffice:shared:event-delivery-read',
  'backoffice:shared:access-decision-read',
  'backoffice:lead:lead-read',
  'backoffice:identity:permission-explain-read',
  'backoffice:identity:member-read',
  'backoffice:identity:member-create',
  'backoffice:identity:member-update',
  'backoffice:identity:member-impersonate',
  'backoffice:identity:role-read',
  'backoffice:admin:read',
  'backoffice:admin:create',
  'backoffice:admin:deactivate',
  'backoffice:admin:preferences-read',
  'backoffice:admin:preferences-update',
  'backoffice:admin:push-device-manage',
  'backoffice:admin:landiq-data-run-manage',
  'backoffice:property:property-read',
  'backoffice:property:document-write',
  'backoffice:property:memory-manage',
  'backoffice:property:document-read',
  'backoffice:property:document-update',
  'backoffice:deal:deal-read',
  'backoffice:deal:deal-restore',
  'backoffice:deal:organization-record-read',
  'backoffice:deal:organization-record-move',
  // Operator label read over Interessenten (IMM-1120, Refined Decision 1): gates an Interessent's label, the Datenraum card (`prospect_data_room`, IMM-1176.2) and the prospect_detail read (IMM-1172) on the admin MCP.
  'backoffice:deal:prospect-read',
  'backoffice:relationship:company-read',
  // Operator label read over Kontakte (IMM-1120, Refined Decision 1): gates a Kontakt's label on the admin MCP; no tool reads a Kontakt with it.
  'backoffice:relationship:contact-read',
  'backoffice:valuation:valuation-request-read',
  'backoffice:valuation:valuation-request-take-on',
  'backoffice:valuation:valuation-request-complete',
  'backoffice:valuation:valuation-request-release',
  'backoffice:valuation:valuation-request-create',
  'backoffice:product-skill:read',
  'backoffice:product-skill:manage',
  'backoffice:fleet:read',
  'backoffice:fleet:command',
  'backoffice:communications:whatsapp-dispatch-read',
  'workspace:identity:organization-create',
  'workspace:identity:organization-coverage-write',
  'workspace:identity:organization-coverage-read',
  'workspace:identity:workspace-role-assign',
  'workspace:identity:organizations-read',
  'workspace:identity:app-handoff-exchange',
  'workspace:identity:organization-read',
  'workspace:identity:my-organizations-read',
  'workspace:identity:profile-read',
  'workspace:identity:profile-update',
  'workspace:identity:preferences-read',
  'workspace:identity:preferences-update',
  'workspace:hlre:area-read',
  'workspace:hlre:partner-lead-read',
  'workspace:hlre:partner-profile-read',
  'workspace:identity:member-read',
  'workspace:identity:member-create',
  'workspace:identity:member-update',
  'workspace:identity:member-profile-read',
  'workspace:identity:member-profile-update',
  'workspace:identity:invitation-read',
  'workspace:identity:invitation-resend',
  'workspace:identity:invitation-revoke',
  'workspace:identity:company-read',
  'workspace:identity:company-update',
  'workspace:identity:branding-read',
  'workspace:identity:branding-update',
  'workspace:identity:document-template-read',
  'workspace:identity:document-template-create',
  'workspace:identity:document-template-update',
  'workspace:identity:document-template-delete',
  'workspace:identity:email-template-read',
  'workspace:identity:email-template-create',
  'workspace:identity:email-template-update',
  'workspace:identity:email-template-delete',
  'workspace:identity:api-key-read',
  'workspace:identity:api-key-create',
  'workspace:identity:api-key-update',
  'workspace:identity:api-key-revoke',
  'workspace:identity:connected-client-create',
  'workspace:identity:connected-client-read',
  'workspace:identity:connected-client-revoke',
  'workspace:identity:mcp-invoke',
  'workspace:relationship:contact-read',
  'workspace:relationship:company-read',
  'workspace:property:property-read',
  'workspace:property:document-read',
  'workspace:property:portal-connection-read',
  'workspace:property:portal-connection-manage',
  'workspace:deal:deal-read',
  'workspace:deal:prospect-read',
  'workspace:lead:lead-read',
  'workspace:relationship:contact-reassign',
  'workspace:property:property-reassign',
  'workspace:deal:deal-reassign',
  'workspace:lead:lead-reassign',
  'workspace:assistant:conversation-use',
  'workspace:assistant:conversation-read',
  'workspace:assistant:capability-gap-record',
  'workspace:assistant:skill-manage',
  'workspace:assistant:skill-read',
  'workspace:communications:mailbox-connect',
  'workspace:communications:mailbox-disconnect',
  'workspace:communications:mailbox-read',
  'workspace:communications:company-mailbox-read',
  'workspace:communications:company-mailbox-manage',
  'workspace:communications:mailbox-thread-read',
  'workspace:communications:event-stream-read',
  'workspace:communications:calendar-read',
  'workspace:communications:calendar-write',
  'workspace:search:search-read',
  'organization:identity:own-profile-read',
  'organization:identity:organization-read',
  'organization:identity:organization-update',
  'organization:identity:member-read',
  'organization:identity:location-member-read',
  'organization:identity:member-create',
  'organization:identity:member-update',
  'organization:identity:member-role-assign',
  'organization:identity:profile-read',
  'organization:identity:profile-update',
  'organization:identity:member-profile-read',
  'organization:identity:member-profile-update',
  'organization:identity:company-read',
  'organization:identity:company-update',
  'organization:identity:app-handoff-create',
  'organization:identity:api-key-read',
  'organization:identity:api-key-create',
  'organization:identity:api-key-update',
  'organization:identity:api-key-revoke',
  'organization:media:file-write',
  'organization:media:file-read',
  'organization:media:file-delete',
  'organization:property:property-create',
  'organization:property:property-read',
  'organization:property:property-update',
  'organization:property:portal-listing-publish',
  'organization:property:portal-listing-withdraw',
  'organization:property:portal-listing-browser-publish',
  'organization:property:property-reassign',
  'organization:property:property-delete',
  'organization:property:property-contact-write',
  'organization:property:document-read',
  'organization:property:document-write',
  'organization:property:document-request',
  'organization:property:document-manage-any',
  'organization:property:media-read',
  'organization:property:media-write',
  'organization:property:media-manage-any',
  'organization:deal:deal-create',
  'organization:deal:deal-read',
  'organization:deal:deal-phase-advance',
  'organization:deal:deal-phase-move-back',
  'organization:deal:deal-update',
  'organization:deal:prospect-create',
  'organization:deal:prospect-phase-advance',
  'organization:deal:prospect-phase-move-back',
  'organization:deal:prospect-update',
  'organization:deal:deal-delete',
  'organization:deal:deal-assign',
  'organization:deal:deal-oversight',
  'organization:deal:document-manage-any',
  'organization:valuation:valuation-request-read',
  'organization:valuation:valuation-request-create',
  'organization:valuation:valuation-request-release',
  'organization:activity:activity-read',
  'organization:activity:activity-update',
  'organization:activity:activity-write',
  'organization:activity:activity-oversight',
  'organization:activity:activity-delete',
  'organization:relationship:contact-create',
  'organization:relationship:contact-read',
  'organization:relationship:contact-update',
  'organization:relationship:contact-delete',
  'organization:relationship:contact-note-update',
  'organization:relationship:contact-note-delete',
  'organization:relationship:contact-communication-update',
  'organization:relationship:contact-communication-delete',
  'organization:relationship:company-create',
  'organization:relationship:company-read',
  'organization:relationship:company-update',
  'organization:relationship:company-archive',
  'organization:relationship:company-unarchive',
  'organization:relationship:company-delete',
  'organization:relationship:company-contact-write',
  'organization:lead:lead-create',
  'organization:lead:lead-read',
  'organization:lead:lead-update',
  'organization:lead:lead-assign',
  'organization:lead:lead-partner-read',
  'organization:lead:lead-partner-manage',
  'organization:communications:mailbox-send',
  'organization:communications:appointment-write',
  'organization:identity:partner-onboard',
  'workspace:deal:buyer-contract-template-read',
  'workspace:deal:buyer-contract-template-create',
  'workspace:deal:buyer-contract-template-update',
  'workspace:deal:buyer-contract-template-delete',
] as const satisfies ReadonlyArray<Exclude<Permission, 'PUBLIC'>>;

/**
 * The six use-case permissions a `vag_` valuation-agent token can be granted (IMM-1402), in catalogue order.
 * No operator holds them: `ADMIN_PERMISSIONS` excludes them and `BACKOFFICE` scope keeps them out of every key.
 */
export const VALUATION_AGENT_PERMISSIONS = [
  'backoffice:valuation:agent-catalog-read',
  'backoffice:valuation:agent-property-read',
  'backoffice:valuation:agent-request-receive',
  'backoffice:valuation:agent-result-deliver',
  'backoffice:valuation:agent-finding-write',
  'backoffice:valuation:agent-capture-photo-read',
] as const satisfies ReadonlyArray<Permission>;
export type ValuationAgentPermission = (typeof VALUATION_AGENT_PERMISSIONS)[number];

/**
 * The subset of `Permission`s a member may grant to a member-bound API
 * key: pushing Owner-Leads in, and reading back the records the web app
 * itself reads. Deliberately an ALLOW-LIST rather than a filter over
 * `PERMISSIONS` — a customer-facing key offers only a slice of what its
 * owning member can do, and key management itself is never grantable (no
 * privilege escalation via a key). Contrast
 * `ADMIN_API_KEY_GRANTABLE_PERMISSIONS`, the derived operator catalogue.
 *
 * Declaration ORDER is published: the issuance dialog groups the entries by
 * their resource segment, reads first within a group, and keeps this order
 * within each of those two partitions. APPEND to widen the catalogue — every literal is
 * stored verbatim in `api_keys.granted_permissions` and keys a label in the
 * i18n dictionary, so reordering churns a customer-facing list and renaming
 * breaks live keys.
 *
 * At issuance the requested set must be a subset of this list *and* of
 * the issuer's own current permissions; at use time a key's effective
 * permissions are its granted set intersected with the bound member's live
 * permissions, so a key never outlives the access of the member behind it.
 * Adding a literal here therefore GRANTS nobody anything — it makes the grant
 * offerable.
 */
export const API_KEY_GRANTABLE_PERMISSIONS = [
  'organization:lead:lead-create',
  // Read a workspace's Einzugsgebiet coverage via the authenticated read API
  // (IMM-415). Read-only and independent of lead-push, so a key can be issued
  // carrying only this — the Salesforce sync's coverage-read grant.
  'workspace:identity:organization-coverage-read',
  // The reads behind the Objekt, Lead, Deal and Aktivität endpoints the web app
  // already calls, so a customer can issue a key that reads their own data
  // (IMM-557.1). List and detail do NOT share a literal — a workspace-addressed
  // list is gated workspace-tier, a single record org-tier — so a key that can
  // list Leads cannot open one unless both grants are ticked. Hence six entries
  // for four resources; the labels carry the distinction.
  'workspace:property:property-read',
  'workspace:lead:lead-read',
  'organization:lead:lead-read',
  'workspace:deal:deal-read',
  'organization:deal:deal-read',
  'organization:activity:activity-read',
  // The reads behind the valuation, document, gallery and property-dossier
  // endpoints IMM-569.4–.7 publish (IMM-569.3). All four are ORGANISATION-tier:
  // each addresses a single record under one Standort, so there is no
  // workspace-tier twin to pair them with the way the IMM-557 list reads have
  // one. `property-read` here is the org-tier literal — it unlocks more than the
  // dossier (the org-addressed property list and detail, and the property↔contact
  // links), which is why its label says »Objekt-Details lesen« rather than naming
  // the dossier.
  'organization:valuation:valuation-request-read',
  'organization:property:document-read',
  'organization:property:media-read',
  'organization:property:property-read',
  // A colleague's agent profile and their portrait (IMM-603). One literal for
  // both reads: the portrait IS part of the profile, so a key that may read one
  // may read the other — and reusing the grant is what lets every key already
  // carrying it reach the new portrait endpoint without being re-issued.
  //
  // Appended because publishing required it, not as a widening in its own right.
  // `openapi.test.ts` refuses to publish a workspace-key leaf whose grant no
  // customer can tick, on the grounds that an endpoint an integrator can never
  // obtain the permission for is a worse surface than an absent one — which is
  // this very ticket's defect, one step earlier. The guard is right, so "publish
  // the profile pair" and "widen the catalogue" are not separable decisions.
  'workspace:identity:member-profile-read',
  // The four writes an MCP client may hold (IMM-601.1): filing activities,
  // editing them (complete / reschedule / cancel ride `activity-update`), and
  // moving a deal along its pipeline. Grantable to ANY workspace key, not only
  // MCP-capable ones — the catalogue is per-key reach, not per-transport — and
  // as everywhere in this list, ticking one grants nothing the issuing member
  // does not already hold. No tool uses them until IMM-601.4/601.5.
  'organization:activity:activity-write',
  'organization:activity:activity-update',
  'organization:deal:deal-phase-advance',
  'organization:deal:deal-phase-move-back',
  // The Standort list the settings page already reads, published for a linked
  // application that has to print a location's postal address (IMM-751.1). The
  // LIST literal, not the single-record `organization:identity:organization-read`
  // — that one is in `MEMBER_IMPLICIT_PERMISSIONS`, so offering it would be a
  // no-op dressed as a boundary. Nothing about the endpoint changes; this line
  // is what makes its grant tickable.
  'workspace:identity:organizations-read',
  // The receiving half of the crossing (IMM-751.2.2). Appended because
  // publishing required it: `openapi.test.ts` refuses a keyed public leaf whose
  // grant no customer can tick, so publishing the exchange and offering its
  // grant are not separable decisions.
  'workspace:identity:app-handoff-exchange',
  // The members of one Standort (IMM-751.3). Its LABEL is deliberately distinct
  // from `member-read`'s — a workspace admin ticking grants has to be able to
  // tell "the people at this location" from "every member of the workspace".
  // Since IMM-873.1 this catalogue offers BOTH, which is what makes that
  // distinction load-bearing rather than merely tidy: the two now sit in the
  // same checkbox list.
  'organization:identity:location-member-read',
  // IMM-769 — the grants behind the member command/query surface a connected
  // client can now reach. A grant that is NOT here is one an `immo_` key cannot
  // be issued with, so advertising its tool's scope would send a client into a
  // consent flow it could never complete — which is why this list and
  // `MCP_SCOPES_SUPPORTED` are asserted against each other.
  //
  // IMM-769.2 — contacts and companies, reads only.
  'workspace:relationship:contact-read',
  'workspace:relationship:company-read',
  'organization:relationship:company-read',
  // IMM-769.3 — the property set.
  'organization:property:property-create',
  'organization:property:property-contact-write',
  'organization:property:property-reassign',
  // IMM-769.4 — the deal set.
  'organization:deal:deal-create',
  'organization:deal:deal-assign',
  'organization:deal:deal-update',
  // IMM-769.5 — the lead set.
  'organization:lead:lead-update',
  'organization:lead:lead-assign',
  // IMM-769.6 — the document request. The one grant here whose tool leaves the
  // workspace, which the tool's `approval_required` classification governs — a
  // key may hold the grant; the client still has to ask before it runs.
  'organization:property:document-request',
  // Appended by IMM-873.1, so the workspace MCP member directory has a grant an
  // `immo_` key can be issued with. Appended, never inserted. It grants nothing
  // anyone did not already hold: `workspace:identity:member-read` is in
  // `MEMBER_IMPLICIT_PERMISSIONS` (IMM-318 — every member may resolve an actor
  // id to a name), and a key's effective set is the intersection of what was
  // granted with what the bound member currently holds.
  'workspace:identity:member-read',
  // IMM-893.1 — filing a document from a connected client. The route DELETE
  // gate shares this literal, so `refuseApiKeyPrincipal` is stacked on the
  // delete route in the same change: a key may file, never destroy.
  'organization:property:document-write',
  // IMM-895.2 — the contact write set: creating, updating, recording a note
  // or communication, and amending one. Appended, never inserted:
  // `MCP_SCOPES_SUPPORTED` is compared element by element against the
  // composed catalogue's order, and the six tools register after the three
  // company reads.
  'organization:relationship:contact-create',
  'organization:relationship:contact-update',
  'organization:relationship:contact-note-update',
  'organization:relationship:contact-communication-update',
  // IMM-895.3 — the company write set: create, replace, archive, unarchive and
  // the roster link writes. Appended, never inserted, for the same reason as
  // the contact writes above: the six tools register after the six contact
  // writes.
  'organization:relationship:company-create',
  'organization:relationship:company-update',
  'organization:relationship:company-archive',
  'organization:relationship:company-unarchive',
  'organization:relationship:company-contact-write',
  // IMM-895.4 — the property writes: the thirteen section edits and the seven
  // lifecycle transitions behind `property_update` / `property_status`.
  // `organization:property:property-contact-write` and
  // `organization:property:document-write` are already above (IMM-769.3 /
  // IMM-893.1), so this slice adds exactly this one grant.
  'organization:property:property-update',
  // Ordering a Bewertung from a connected client (IMM-756.1, reshaped by
  // IMM-891.2 into `valuation_start` / `valuation_amend` / `valuation_order`).
  // The literal ALSO gates two DELETE routes (the draft and its attachments), so
  // `refuseApiKeyPrincipal` is stacked on both: a key may order, never destroy.
  'organization:valuation:valuation-request-create',
  // IMM-1064.8 — the Verkaufs-Pipeline from a connected client: reading the
  // Interessenten, creating one and moving it forward or back. `prospect-update`
  // (mark-lost, offer status) stays closed to keys, see the IMM-1172.1 entry below. Appended, never inserted.
  'workspace:deal:prospect-read',
  'organization:deal:prospect-create',
  'organization:deal:prospect-phase-advance',
  'organization:deal:prospect-phase-move-back',
  // IMM-1161.2 — publishing and withdrawing the web exposé from a connected client
  // (`web_expose_publish` / `web_expose_withdraw`). The literals also gate the ImmoScout24
  // and OpenImmo transmit and withdraw routes, which `refuseApiKeyPrincipal` closes to a key.
  'organization:property:portal-listing-publish',
  'organization:property:portal-listing-withdraw',
  // IMM-1156.1 — re-filing any property document from a connected client. Grantable
  // to a key, effective only where the bound member holds it at the Standort the
  // call names. Appended, never inserted.
  'organization:property:document-manage-any',
  // IMM-1165.3 / IMM-1164.3 — video_link_add and tour_add. Every HTTP route this literal gates (upload, confirm, add-by-link, rename, recategorise, cover image, publish, reorder, delete, promote-to-gallery, tour writes) refuses an API key, so the grant's only reach is the add tools.
  'organization:property:media-write',
  // IMM-270.4 — reading the workspace's Vorlagen from a connected client
  // (`document_template_list`, `document_template_get`). Appended, never
  // inserted. It grants nothing anyone lacked: the literal is in
  // `MEMBER_IMPLICIT_PERMISSIONS` (IMM-270.1, AC 41), and a key's effective set
  // is the intersection of its grants with the bound member's live permissions.
  'workspace:identity:document-template-read',
  // IMM-1177.1 — reading the workspace's E-Mail-Vorlagen from a connected client
  // (`email_template_list`, `email_template_get`). Appended, never inserted. It
  // grants nothing anyone lacked: the literal is in `MEMBER_IMPLICIT_PERMISSIONS`,
  // and a key's effective set is its grants intersected with the bound member's
  // live permissions.
  'workspace:identity:email-template-read',
  // IMM-1177.2 — creating and editing E-Mail-Vorlagen from a connected client
  // (`email_template_create`, `email_template_update`). Both tools are
  // `approval_required`, and a key's effective set is its grants intersected with
  // the bound member's live permissions. Appended, never inserted.
  'workspace:identity:email-template-create',
  'workspace:identity:email-template-update',
  // IMM-1176.2 — releasing and withdrawing the Datenraum from a connected client
  // (`prospect_data_room_release` / `_withdraw`, both approval_required) and, from IMM-1172, recording an
  // offer and changing its status. Appended, never inserted. The literal also gates mark-lost and an
  // offer's status change on the REST routes, which stay closed to keys.
  'organization:deal:prospect-update',
  // IMM-1381 — the browser extension's write (ADR 0064): its job and its outcome. Issued by „Erweiterung
  // verbinden“ together with `workspace:property:property-read`. Appended, never inserted.
  'organization:property:portal-listing-browser-publish',
] as const satisfies ReadonlyArray<Permission>;

/**
 * The assistant grants only the extension's key may hold (IMM-1393, ADR 0067). Outside the exact
 * `BROWSER_EXTENSION_KEY_PERMISSIONS` set they are not grantable to any key.
 */
export const BROWSER_EXTENSION_ONLY_PERMISSIONS = [
  'workspace:assistant:conversation-use',
  'workspace:assistant:conversation-read',
] as const satisfies ReadonlyArray<Permission>;

/**
 * The grants of the key „Erweiterung verbinden" issues (IMM-1381, ADR 0064): the extension's listings menu, the
 * job and outcome of a write in the agent's ImmoScout24 tab, and the KI-Chat (IMM-1393, ADR 0067). Nothing else,
 * so a lost key reaches nothing else. A key whose stored grants are exactly this set IS the extension key.
 */
export const BROWSER_EXTENSION_KEY_PERMISSIONS = [
  'workspace:property:property-read',
  'organization:property:portal-listing-browser-publish',
  ...BROWSER_EXTENSION_ONLY_PERMISSIONS,
] as const satisfies ReadonlyArray<Permission>;

/**
 * Grants the extension key carries on top of {@link BROWSER_EXTENSION_KEY_PERMISSIONS} when the connecting member
 * holds them (IMM-1484: importing ImmoScout24 listings as Objekte). A member who may not create Objekte still
 * connects, and a key issued before the import keeps working; it simply cannot import.
 */
export const BROWSER_EXTENSION_OPTIONAL_KEY_PERMISSIONS = [
  'organization:property:property-create',
] as const satisfies ReadonlyArray<Permission>;

export function isBrowserExtensionGrantSet(granted: ReadonlyArray<string>): boolean {
  const set = new Set(granted);
  const optional: ReadonlyArray<string> = BROWSER_EXTENSION_OPTIONAL_KEY_PERMISSIONS;
  const extras = [...set].filter(
    (permission) => !(BROWSER_EXTENSION_KEY_PERMISSIONS as ReadonlyArray<string>).includes(permission),
  );

  return (
    BROWSER_EXTENSION_KEY_PERMISSIONS.every((permission) => set.has(permission)) &&
    extras.every((permission) => optional.includes(permission))
  );
}

export type GrantablePermission = (typeof API_KEY_GRANTABLE_PERMISSIONS)[number];

/**
 * The three authorisation scopes from [ADR 0029](../../../.docs/engineering/adr/0029-rbac-permission-model.md).
 * A `WORKSPACE` permission is resolved against the acting user's workspace
 * authority (fixed workspace roles); an `ORG` permission is resolved against
 * the member's organisation authority; a `BACKOFFICE` permission belongs to the
 * separate admin / back-office RBAC namespace (not organisation-bound), gated by
 * the admin surface rather than the workspace/org resolver. The scope is a
 * property of the literal — encoded in its first segment — so the gate routes by
 * it and the namespaces never mix.
 */
export type PermissionScope = 'WORKSPACE' | 'ORG' | 'BACKOFFICE';

/**
 * Derives the authorisation scope from a permission literal's first segment:
 * `workspace:` → `WORKSPACE`, `organization:` → `ORG`, `backoffice:` →
 * `BACKOFFICE`. No hand-maintained scope map: the literal carries its own scope.
 *
 * `PUBLIC` bypasses the gate entirely (scope is only consulted past the gate),
 * so it takes the benign `ORG` default.
 */
export function permissionScope(permission: Permission): PermissionScope {
  if (permission === 'PUBLIC') {
    return 'ORG';
  }

  const prefix = permission.slice(0, permission.indexOf(':'));

  switch (prefix) {
    case 'workspace':
      return 'WORKSPACE';
    case 'backoffice':
      return 'BACKOFFICE';
    case 'organization':
      return 'ORG';
    default:
      return 'ORG';
  }
}

/** The HLRE-area literals live only in the HLRE workspace's role bundles; no partner role holds one. */
export function isHlrePermission(permission: Permission): boolean {
  return permission.startsWith('workspace:hlre:');
}

/**
 * Every `organization:`-scoped permission. Backs the `organisation-owner` role and
 * the membership resolver's `grantsGlobalAccess` virtual full grant (ADR 0029).
 *
 * Lives HERE, beside `PERMISSIONS` and `permissionScope` it is derived from, rather
 * than in the identity module that owns the role catalogue — because a consumer
 * outside identity needs it too. A workspace-addressed read that resolves an
 * `organization:`-scoped capability per Standort recognises the global grant by the
 * resolver returning this whole set, and the module boundary (ADR 0007) forbids it
 * importing identity's copy. Two derivations of one filter would agree on every
 * input until someone changed one of them — BL-089's shape — so there is one, and
 * identity re-exports it.
 */
export const ALL_ORG_PERMISSIONS: ReadonlyArray<Permission> = PERMISSIONS.filter(
  (permission) => permissionScope(permission) === 'ORG',
);

/**
 * Any api-key-management permission — create, revoke, read, or update a key, in
 * ANY scope (`workspace:identity:api-key-*`, `organization:identity:api-key-*`,
 * …) — is NEVER grantable to an API key, so a leaked key cannot escalate into
 * more keys. Matched STRUCTURALLY on the `identity:api-key-` resource rather than
 * a hand-listed set: a new scope or action can never silently slip into the
 * grantable catalogue (IMM-435). This is the single maintained fact behind
 * `ADMIN_API_KEY_GRANTABLE_PERMISSIONS`.
 */
const isApiKeyManagementPermission = (permission: Permission): boolean => permission.includes(':identity:api-key-');

/**
 * Connecting, listing and revoking the caller's OWN MCP clients — the OAuth
 * consent surface. Session-only like `app-handoff-create`: a consent names the
 * signed-in person as the one the client will act for, so a key holding it
 * would have nobody to consent as. Matched structurally for the same reason as
 * the api-key literals above.
 */
const isConnectedClientPermission = (permission: Permission): boolean =>
  permission.includes(':identity:connected-client-');

/**
 * The permissions an immoOS operator may grant to an admin-issued workspace API
 * key (IMM-435). DERIVED, never hand-maintained: every `WORKSPACE`- or `ORG`-
 * scoped permission EXCEPT the three api-key-management literals — so a newly
 * minted permission is grantable automatically and the only maintained fact is
 * the key-management exclusion. `backoffice:*` permissions are excluded by
 * construction (they are neither `WORKSPACE` nor `ORG`), keeping admin-plane
 * authority out of a workspace-scoped key.
 *
 * Deliberately SEPARATE from the customer `API_KEY_GRANTABLE_PERMISSIONS`: the
 * customer flow keeps its narrow lead-creation allow-list, the operator flow
 * offers the full workspace/org surface minus key management, and neither
 * derives from the other. At issuance the requested set must be a subset of
 * this catalogue AND of the bound owner's live permissions.
 *
 * NOTE (IMM-572.1): despite the name, this governs the permissions an operator
 * may grant on a WORKSPACE key — NOT an admin API key (the operator-bound
 * credential whose kind IMM-572.1 renamed from 'service' to 'admin'). Admin API
 * keys hold no permissions of their own; they act as their bound operator. The
 * name is retained deliberately to keep that rename a pure token swap; the two
 * adjacent "admin API key" meanings are an accepted, flagged trade-off.
 */
export const ADMIN_API_KEY_GRANTABLE_PERMISSIONS: ReadonlyArray<Permission> = PERMISSIONS.filter(
  (permission) =>
    (permissionScope(permission) === 'WORKSPACE' || permissionScope(permission) === 'ORG') &&
    !isApiKeyManagementPermission(permission) &&
    !isConnectedClientPermission(permission) &&
    !isHlrePermission(permission) &&
    // Never grantable by anyone (IMM-601.1): the MCP transport permission is
    // injected by the verifier for MCP-capable keys only. Stored on a key's
    // grants it would survive the flag, so it is excluded here exactly like
    // key management is.
    permission !== 'workspace:identity:mcp-invoke' &&
    // Minting a handoff is a SESSION-only capability (IMM-751.2.1): the token is
    // an identity assertion saying which signed-in member is crossing, so a
    // credential with no member session behind it has nobody to assert.
    //
    // Excluded EXPLICITLY, not by tier. It is `organization:`-scoped, so this
    // derived catalogue would otherwise pick it up automatically and an
    // operator-issued `immo_` key — which authenticates as `kind: 'workspace'`
    // and whose bound member may be an organisation owner — could mint live
    // crossings with no browser anywhere in the loop. Leaving it out of the
    // CUSTOMER catalogue is not enough on its own, because that list is
    // hand-maintained and this one is derived.
    permission !== 'organization:identity:app-handoff-create' &&
    // Binding a portal account is a member-session action (IMM-1057.1): the
    // route refuses API-key principals, so a key holding it would be dead
    // authority.
    permission !== 'workspace:property:portal-connection-manage' &&
    // Publishing and withdrawing are member-session actions (IMM-1057.3): the
    // operator catalogue is derived from PERMISSIONS and would otherwise pick
    // both up.
    permission !== 'organization:property:portal-listing-publish' &&
    permission !== 'organization:property:portal-listing-withdraw',
);
